import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { sendPasswordResetAction, loadingToggleAction, socialLoginAction } from '../../store/actions/AuthActions';
import { checkUserSignInMethods } from '../../services/AuthService';
import { Mail, ArrowLeft, AlertCircle, CheckCircle, Info } from 'lucide-react';
import useWindowSize, { isMobile } from '../../hooks/useWindowSize';

// image
import loginbg from "../../images/bg-login.jpg";

const styles = {
  loginMainPage: {
    minHeight: '100vh',
    display: 'flex',
    backgroundImage: `url(${loginbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  loginWrapper: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center'
  },
  authCard: {
    background: 'rgba(255, 255, 255, 0.98)',
    borderRadius: '16px',
    padding: '3rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '450px',
    border: '1px solid #e5e7eb',
    backdropFilter: 'blur(10px)'
  },
  formGroup: {
    marginBottom: '1.5rem',
    position: 'relative'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  formInput: {
    width: '100%',
    padding: '0.875rem 1rem 0.875rem 3rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa'
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    color: '#9ca3af',
    zIndex: 2
  },
  submitButton: {
    width: '100%',
    padding: '0.875rem',
    background: 'linear-gradient(135deg, #057e38 0%, var(--bs-primary) 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    marginTop: '1rem'
  },
  googleButton: {
    width: '100%',
    padding: '0.875rem',
    background: '#4285f4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '2rem',
    transition: 'color 0.2s ease'
  },
  alertMessage: {
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.875rem'
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  },
  successAlert: {
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0'
  },
  infoAlert: {
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    border: '1px solid #bfdbfe'
  },
  warningAlert: {
    backgroundColor: '#fffbeb',
    color: '#d97706',
    border: '1px solid #fed7aa'
  },
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Mobile-specific style overrides (applied when screen ≤ 768px)
const mobileStyles = {
  loginWrapper: {
    padding: '1rem'
  },
  authCard: {
    padding: '2rem 1.5rem',
    boxShadow: 'none',
    borderRadius: '8px'
  },
  formInput: {
    fontSize: '16px' // Prevents zoom on iOS
  }
};

// Helper function to merge mobile styles
const getResponsiveStyle = (baseStyle, mobileStyle, isMobile) => {
  return isMobile && mobileStyle ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

function ForgotPassword(props) {
  // Mobile responsiveness
  const { width } = useWindowSize();
  const mobile = isMobile(width);
  
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountInfo, setAccountInfo] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();

  // Security: Input sanitization
  const sanitizeInput = (input) => {
    return input.trim().substring(0, 254); // RFC 5321 email limit
  };

  // Security: Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setAccountInfo(null);

    const sanitizedEmail = sanitizeInput(email);

    // Enhanced validation
    if (!sanitizedEmail) {
      setError('E-postadress krävs');
      return;
    }

    if (!isValidEmail(sanitizedEmail)) {
      setError('Ogiltig e-postadress');
      return;
    }

    setIsLoading(true);
    dispatch(loadingToggleAction(true));

    try {
      // First, check what sign-in methods are available for this email
      const signInMethods = await checkUserSignInMethods(sanitizedEmail);

      if (signInMethods.isEmpty && !signInMethods.error) {
        // No account found
        setError('Ingen användare hittades med den e-postadressen');
        setIsLoading(false);
        dispatch(loadingToggleAction(false));
        return;
      }

      if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
        // Google-only account
        setAccountInfo({
          type: 'google-only',
          email: sanitizedEmail,
          hasGoogle: true,
          hasPassword: false
        });
        setIsLoading(false);
        dispatch(loadingToggleAction(false));
        return;
      }

      if (!signInMethods.hasPassword && signInMethods.methods.length > 0) {
        // Account exists but no password
        setAccountInfo({
          type: 'no-password',
          email: sanitizedEmail,
          methods: signInMethods.methods
        });
        setIsLoading(false);
        dispatch(loadingToggleAction(false));
        return;
      }

      // Account has password - proceed with reset
      await dispatch(sendPasswordResetAction(sanitizedEmail));
      setIsSubmitted(true);

    } catch (error) {
      console.error('Password reset error:', error);
      
      // Handle specific errors from our enhanced AuthService
      if (error.code === 'auth/google-only-user') {
        setAccountInfo({
          type: 'google-only',
          email: sanitizedEmail,
          hasGoogle: true,
          hasPassword: false
        });
      } else if (error.code === 'auth/no-password-method') {
        setAccountInfo({
          type: 'no-password',
          email: sanitizedEmail,
          methods: error.availableMethods || []
        });
      } else if (error.code === 'auth/user-not-found') {
        setError('Ingen användare hittades med den e-postadressen');
      } else if (error.code === 'auth/invalid-email') {
        setError('Ogiltig e-postadress');
      } else if (error.code === 'auth/too-many-requests') {
        setError('För många försök. Vänta en stund och försök igen');
      } else {
        setError(error.message || 'Ett fel inträffade vid skickande av återställningslänk');
      }
    } finally {
      setIsLoading(false);
      dispatch(loadingToggleAction(false));
    }
  }

  function handleGoogleSignIn() {
    setIsLoading(true);
    dispatch(loadingToggleAction(true));
    dispatch(socialLoginAction('google', history, '/'));
  }

  React.useEffect(() => {
    if (!props.showLoading) {
      setIsLoading(false);
    }
  }, [props.showLoading]);

  // Success state
  if (isSubmitted) {
    return (
      <>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>

        <div style={styles.loginMainPage} data-testid="forgot-password-success-page">
          <div style={getResponsiveStyle(styles.loginWrapper, mobileStyles.loginWrapper, mobile)}>
            <div style={getResponsiveStyle(styles.authCard, mobileStyles.authCard, mobile)} data-testid="success-card">
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#16a34a',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }} data-testid="success-icon">
                  <CheckCircle size={32} color="white" />
                </div>

                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}
                    data-testid="success-title">
                  Återställningslänk skickad!
                </h2>

                <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.6 }}
                   data-testid="success-message">
                  Vi har skickat en återställningslänk till <strong>{email}</strong>.
                  Kontrollera din inkorg och klicka på länken för att återställa ditt lösenord.
                </p>

                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '2rem' }}
                   data-testid="success-additional-info">
                  Ser du inte e-posten? Kontrollera din skräppost eller försök igen om några minuter.
                </p>

                <Link
                  to="/login"
                  style={{
                    ...styles.submitButton,
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                  data-testid="back-to-login-button"
                >
                  Tillbaka till inloggning
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          background-color: white;
        }
        
        .submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
        }
        
        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .google-button:hover:not(:disabled) {
          background-color: #3367d6;
          transform: translateY(-1px);
          box-shadow: 0 10px 25px rgba(66, 133, 244, 0.3);
        }
        
        .google-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .back-button:hover {
          color: var(--bs-primary);
        }
        
        .error-input {
          border-color: #dc2626 !important;
          background-color: #fef2f2 !important;
        }
      `}</style>

      <div style={styles.loginMainPage} data-testid="forgot-password-page">
        <div style={getResponsiveStyle(styles.loginWrapper, mobileStyles.loginWrapper, mobile)}>
          <div style={getResponsiveStyle(styles.authCard, mobileStyles.authCard, mobile)} data-testid="forgot-password-card">
            <Link to="/login" className="back-button" style={styles.backButton} data-testid="back-to-login-link">
              <ArrowLeft size={16} />
              Tillbaka till inloggning
            </Link>

            <div style={{ marginBottom: '2rem', textAlign: 'center' }} data-testid="forgot-password-header">
              <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
                Glömt lösenord?
              </h2>
              <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                Ange din e-postadress så hjälper vi dig att komma in
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{ ...styles.alertMessage, ...styles.errorAlert }} data-testid="error-message" role="alert">
                <AlertCircle size={20} />
                {error}
              </div>
            )}

            {/* Google-only Account Info */}
            {accountInfo?.type === 'google-only' && (
              <div style={{ ...styles.alertMessage, ...styles.infoAlert }} data-testid="google-only-info" role="alert">
                <Info size={20} />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                    Detta konto använder Google för inloggning
                  </div>
                  <div style={{ fontSize: '0.825rem' }}>
                    Kontot <strong>{accountInfo.email}</strong> har inget lösenord. 
                    Logga in med Google istället.
                  </div>
                </div>
              </div>
            )}

            {/* No Password Method Info */}
            {accountInfo?.type === 'no-password' && (
              <div style={{ ...styles.alertMessage, ...styles.warningAlert }} data-testid="no-password-info" role="alert">
                <AlertCircle size={20} />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                    Kontot har inget lösenord
                  </div>
                  <div style={{ fontSize: '0.825rem' }}>
                    Detta konto använder andra inloggningsmetoder. 
                    Kontakta support för hjälp.
                  </div>
                </div>
              </div>
            )}

            {/* Show Google login for Google-only accounts */}
            {accountInfo?.type === 'google-only' ? (
              <button
                type="button"
                className="google-button"
                style={styles.googleButton}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                data-testid="google-signin-button"
              >
                {isLoading ? (
                  <div style={styles.loadingSpinner} data-testid="google-loading-spinner"></div>
                ) : (
                  <i className="fab fa-google" style={{ fontSize: '1.1rem' }}></i>
                )}
                Fortsätt med Google
              </button>
            ) : (
              /* Regular password reset form */
              <form onSubmit={onSubmit} data-testid="forgot-password-form" role="form" noValidate>
                <div style={styles.formGroup}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="email-input">
                    E-postadress
                  </label>
                  <div style={styles.inputWrapper}>
                    <Mail size={20} style={styles.inputIcon} />
                    <input
                      type="email"
                      id="email-input"
                      className={`form-input ${error ? 'error-input' : ''}`}
                      style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="din@epost.se"
                      disabled={isLoading}
                      maxLength="254"
                      autoComplete="email"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  style={styles.submitButton}
                  disabled={isLoading}
                  data-testid="submit-button"
                >
                  {isLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={styles.loadingSpinner} data-testid="loading-spinner"></div>
                      Kontrollerar konto...
                    </div>
                  ) : (
                    'Skicka återställningslänk'
                  )}
                </button>
              </form>
            )}

            {/* Info section */}
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '2rem',
              fontSize: '0.875rem',
              color: '#64748b'
            }} data-testid="tips-section">
              <strong style={{ color: '#475569', display: 'block', marginBottom: '0.5rem' }}>
                💡 Tips
              </strong>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>Om du registrerade dig med Google första gången, använd "Fortsätt med Google"</li>
                <li>Kontrollera din skräppost om du inte ser återställnings-e-posten</li>
                <li>Återställningslänkar är giltiga i 1 timme</li>
              </ul>
            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem' }} data-testid="login-link-section">
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Kom du ihåg ditt lösenord?{' '}
                <Link
                  to="/login"
                  style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}
                  data-testid="login-link"
                >
                  Logga in här
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    errorMessage: state.auth.errorMessage,
    showLoading: state.auth.showLoading,
  };
};

export default connect(mapStateToProps)(ForgotPassword);




// import React, { useState } from 'react';
// import { connect, useDispatch } from 'react-redux';
// import { Link, useHistory } from 'react-router-dom';
// import { sendPasswordResetAction, loadingToggleAction } from '../../store/actions/AuthActions';
// import { Mail, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';

// // image
// import loginbg from "../../images/bg-login.jpg";

// const styles = {
//   loginMainPage: {
//     minHeight: '100vh',
//     display: 'flex',
//     backgroundImage: `url(${loginbg})`,
//     backgroundSize: 'cover',
//     backgroundPosition: 'center',
//     backgroundRepeat: 'no-repeat'
//   },
//   loginWrapper: {
//     display: 'flex',
//     width: '100%',
//     minHeight: '100vh',
//     alignItems: 'center',
//     justifyContent: 'center'
//   },
//   authCard: {
//     background: 'rgba(255, 255, 255, 0.98)',
//     borderRadius: '16px',
//     padding: '3rem',
//     boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
//     width: '100%',
//     maxWidth: '450px',
//     border: '1px solid #e5e7eb',
//     backdropFilter: 'blur(10px)'
//   },
//   formGroup: {
//     marginBottom: '1.5rem',
//     position: 'relative'
//   },
//   inputWrapper: {
//     position: 'relative',
//     display: 'flex',
//     alignItems: 'center'
//   },
//   formInput: {
//     width: '100%',
//     padding: '0.875rem 1rem 0.875rem 3rem',
//     border: '2px solid #e5e7eb',
//     borderRadius: '8px',
//     fontSize: '1rem',
//     transition: 'all 0.2s ease',
//     backgroundColor: '#fafafa'
//   },
//   inputIcon: {
//     position: 'absolute',
//     left: '1rem',
//     color: '#9ca3af',
//     zIndex: 2
//   },
//   submitButton: {
//     width: '100%',
//     padding: '0.875rem',
//     background: 'linear-gradient(135deg, #057e38 0%, var(--bs-primary) 100%)',
//     color: 'white',
//     border: 'none',
//     borderRadius: '8px',
//     fontSize: '1rem',
//     fontWeight: '600',
//     transition: 'all 0.2s ease',
//     marginTop: '1rem'
//   },
//   backButton: {
//     display: 'flex',
//     alignItems: 'center',
//     gap: '0.5rem',
//     color: '#6b7280',
//     textDecoration: 'none',
//     fontSize: '0.875rem',
//     fontWeight: '500',
//     marginBottom: '2rem',
//     transition: 'color 0.2s ease'
//   },
//   alertMessage: {
//     padding: '0.875rem 1rem',
//     borderRadius: '8px',
//     marginBottom: '1.5rem',
//     display: 'flex',
//     alignItems: 'center',
//     gap: '0.75rem',
//     fontSize: '0.875rem'
//   },
//   errorAlert: {
//     backgroundColor: '#fef2f2',
//     color: '#dc2626',
//     border: '1px solid #fecaca'
//   },
//   successAlert: {
//     backgroundColor: '#f0fdf4',
//     color: '#16a34a',
//     border: '1px solid #bbf7d0'
//   },
//   loadingSpinner: {
//     width: '20px',
//     height: '20px',
//     border: '2px solid rgba(255, 255, 255, 0.3)',
//     borderTop: '2px solid currentColor',
//     borderRadius: '50%',
//     animation: 'spin 1s linear infinite'
//   }
// };

// function ForgotPassword(props) {
//   const [email, setEmail] = useState('');
//   const [isSubmitted, setIsSubmitted] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState('');

//   const dispatch = useDispatch();
//   const history = useHistory();

//   function onSubmit(e) {
//     e.preventDefault();
//     setError('');

//     if (!email) {
//       setError('E-postadress krävs');
//       return;
//     }

//     if (!/\S+@\S+\.\S+/.test(email)) {
//       setError('Ogiltig e-postadress');
//       return;
//     }

//     setIsLoading(true);
//     dispatch(sendPasswordResetAction(email))
//       .then(() => {
//         setIsSubmitted(true);
//         setIsLoading(false);
//       })
//       .catch(() => {
//         setIsLoading(false);
//       });
//   }

//   React.useEffect(() => {
//     if (!props.showLoading) {
//       setIsLoading(false);
//     }
//   }, [props.showLoading]);

//   if (isSubmitted) {
//     return (
//       <>
//         <style>{`
//           @keyframes spin {
//             0% { transform: rotate(0deg); }
//             100% { transform: rotate(360deg); }
//           }
//         `}</style>

//         <div style={styles.loginMainPage}>
//           <div style={styles.loginWrapper}>
//             <div style={styles.authCard}>
//               <div style={{ textAlign: 'center' }}>
//                 <div style={{
//                   width: '64px',
//                   height: '64px',
//                   backgroundColor: '#16a34a',
//                   borderRadius: '50%',
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                   margin: '0 auto 1.5rem'
//                 }}>
//                   <CheckCircle size={32} color="white" />
//                 </div>

//                 <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
//                   Återställningslänk skickad!
//                 </h2>

//                 <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.6 }}>
//                   Vi har skickat en återställningslänk till <strong>{email}</strong>.
//                   Kontrollera din inkorg och klicka på länken för att återställa ditt lösenord.
//                 </p>

//                 <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '2rem' }}>
//                   Ser du inte e-posten? Kontrollera din skräppost eller försök igen om några minuter.
//                 </p>

//                 <Link
//                   to="/login"
//                   style={{
//                     ...styles.submitButton,
//                     textDecoration: 'none',
//                     display: 'inline-block'
//                   }}
//                 >
//                   Tillbaka till inloggning
//                 </Link>
//               </div>
//             </div>
//           </div>
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <style>{`
//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }
        
//         .form-input:focus {
//           outline: none;
//           border-color: var(--bs-primary);
//           box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
//           background-color: white;
//         }
        
//         .submit-button:hover:not(:disabled) {
//           transform: translateY(-1px);
//           box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
//         }
        
//         .submit-button:disabled {
//           opacity: 0.6;
//           cursor: not-allowed;
//           transform: none;
//         }
        
//         .back-button:hover {
//           color: var(--bs-primary);
//         }
        
//         .error-input {
//           border-color: #dc2626 !important;
//           background-color: #fef2f2 !important;
//         }
//       `}</style>

//       <div style={styles.loginMainPage}>
//         <div style={styles.loginWrapper}>
//           <div style={styles.authCard}>
//             <Link to="/login" className="back-button" style={styles.backButton}>
//               <ArrowLeft size={16} />
//               Tillbaka till inloggning
//             </Link>

//             <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
//               <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
//                 Glömt lösenord?
//               </h2>
//               <p style={{ color: '#6b7280', fontSize: '1rem' }}>
//                 Ange din e-postadress så skickar vi en återställningslänk
//               </p>
//             </div>

//             {/* Error Message */}
//             {(error || props.errorMessage) && (
//               <div style={{ ...styles.alertMessage, ...styles.errorAlert }}>
//                 <AlertCircle size={20} />
//                 {error || props.errorMessage}
//               </div>
//             )}

//             <form onSubmit={onSubmit}>
//               <div style={styles.formGroup}>
//                 <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
//                   E-postadress
//                 </label>
//                 <div style={styles.inputWrapper}>
//                   <Mail size={20} style={styles.inputIcon} />
//                   <input
//                     type="email"
//                     className={`form-input ${error ? 'error-input' : ''}`}
//                     style={styles.formInput}
//                     value={email}
//                     onChange={(e) => setEmail(e.target.value)}
//                     placeholder="din@epost.se"
//                     disabled={isLoading}
//                   />
//                 </div>
//               </div>

//               <button
//                 type="submit"
//                 className="submit-button"
//                 style={styles.submitButton}
//                 disabled={isLoading}
//               >
//                 {isLoading ? (
//                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
//                     <div style={styles.loadingSpinner}></div>
//                     Skickar återställningslänk...
//                   </div>
//                 ) : (
//                   'Skicka återställningslänk'
//                 )}
//               </button>
//             </form>

//             <div style={{ textAlign: 'center', marginTop: '2rem' }}>
//               <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
//                 Kom du ihåg ditt lösenord?{' '}
//                 <Link
//                   to="/login"
//                   style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}
//                 >
//                   Logga in här
//                 </Link>
//               </p>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// const mapStateToProps = (state) => {
//   return {
//     errorMessage: state.auth.errorMessage,
//     showLoading: state.auth.showLoading,
//   };
// };

// export default connect(mapStateToProps)(ForgotPassword);



// import React from "react";
// import { Link } from "react-router-dom";
// // image
// import logo from "../../images/logo-full.png";
// const ForgotPassword = ({ history }) => {
//   const onSubmit = (e) => {
//     e.preventDefault();
//     history.push("/dashboard");
//   };
//   return (
//     <div className="authincation h-100 p-meddle">
//       <div className="container h-100">
//         {" "}
//         <div className="row justify-content-center h-100 align-items-center">
//           <div className="col-md-6">
//             <div className="authincation-content">
//               <div className="row no-gutters">
//                 <div className="col-xl-12">
//                   <div className="auth-form">
//                     <div className="text-center mb-3">
//                       <Link to="/dashboard">
//                         <img src={logo} alt="" />
//                       </Link>
//                     </div>
//                     <h4 className="text-center mb-4 ">Forgot Password</h4>
//                     <form onSubmit={(e) => onSubmit(e)}>
//                       <div className="form-group">
//                         <label className="">
//                           <strong>Email</strong>
//                         </label>
//                         <input
//                           type="email"
//                           className="form-control"
//                           defaultValue="hello@example.com"
//                         />
//                       </div>
//                       <div className="text-center">
//                         <button
//                           type="submit"
//                           className="btn btn-primary btn-block"
//                         >
//                           SUBMIT
//                         </button>
//                       </div>
//                     </form>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ForgotPassword;
